import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkInTransitToFulfillmentCenterResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for marking a vehicle as in transit to fulfillment center
 */
export interface MarkInTransitToFulfillmentCenterInput {
	orderId: string;
	transitToFulfillmentCenterDetails: {
		estimatedDeliveryDateToFulfillmentCenter: number;
		vehicleTrackingReference?: string;
		contactNumber?: string;
		companyName?: string;
	};
}

/**
 * API function to mark a vehicle as in transit to fulfillment center
 */
export const markInTransitToFulfillmentCenter = async (
	servicesProviderId: string,
	{ orderId, transitToFulfillmentCenterDetails }: MarkInTransitToFulfillmentCenterInput,
): Promise<MarkInTransitToFulfillmentCenterResponse> => {
	const response = await apiClient.post<MarkInTransitToFulfillmentCenterResponse>(
		ordersEndpoints.markInTransitToFulfillmentCenter(servicesProviderId, orderId),
		{ transitToFulfillmentCenterDetails },
	);
	return response.data;
};

/**
 * Hook for marking a purchased vehicle (order) as in transit to fulfillment center
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkInTransitToFulfillmentCenter = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: Omit<MarkInTransitToFulfillmentCenterInput, 'orderId'>) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark as in transit without an order ID or services provider ID.',
				);
			}
			return markInTransitToFulfillmentCenter(servicesProviderId, {
				orderId,
				transitToFulfillmentCenterDetails: input.transitToFulfillmentCenterDetails,
			});
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER);
		},
	});
};
